import {
  ACCOUNT_BLOCKED_ERROR_CODE,
  AxiosErrorResponse,
  LOGIN_BLOCKED_ERROR_CODE,
  PENDING_AUTH_TOAST_KEY,
} from "@/@types/Errors";
import { ILogin } from "@/@types/Login";
import { useAuth } from "@/auth/Auth";
import { useToast } from "@/hooks/use-toast";
import {
  getAccountLoginBody,
  isLoginTwoFactorRequired,
} from "@/services/auth.service";
import { z } from "@/translations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  isInvitationRedirect,
  setPostLoginRedirect,
} from "@/utils/postLoginRedirect";

const RESEND_COOLDOWN_SEC = 60;

const createLoginSchema = (requiredMessage: string, emailMessage: string) =>
  z.object({
    email: z
      .string()
      .min(1, { message: requiredMessage })
      .email({ message: emailMessage }),
    password: z.string().min(8, { message: requiredMessage }),
  });

type LoginStep = "credentials" | "two_factor";

type TwoFactorState = {
  challengeId: string;
  expiresIn: number;
  email: string;
  password: string;
};

function extractLoginErrorMessage(error: AxiosError<AxiosErrorResponse>): string {
  const data = error.response?.data;
  if (data?.errors) {
    const firstError = Object.values(data.errors)[0];
    if (Array.isArray(firstError) && firstError[0]) {
      return String(firstError[0]);
    }
    if (typeof firstError === "string") {
      return firstError;
    }
  }
  if (data?.message) {
    return String(data.message);
  }
  return "";
}

export const useLogin = () => {
  const { t } = useTranslation(["validation", "common", "errors"]);
  const location = useLocation();
  const navigate = useNavigate();
  const { signInAccount } = useAuth();
  const { toast } = useToast();

  const LoginSchema = createLoginSchema(
    t("required_field"),
    t("email_invalid"),
  );

  const inviteEmail = (location.state as any)?.inviteEmail as string | undefined;
  const inviteToken = (location.state as any)?.inviteToken as string | undefined;

  const [step, setStep] = React.useState<LoginStep>("credentials");
  const [twoFactorState, setTwoFactorState] =
    React.useState<TwoFactorState | null>(null);
  const [twoFactorError, setTwoFactorError] = React.useState<string | null>(null);
  const [resendCooldownSec, setResendCooldownSec] = React.useState(0);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<ILogin>({
    resolver: zodResolver(LoginSchema),
    mode: "onSubmit",
    defaultValues: {
      email: inviteEmail || "",
    },
  });

  const navigatedRef = React.useRef(false);

  React.useEffect(() => {
    const pendingToast = sessionStorage.getItem(PENDING_AUTH_TOAST_KEY);
    if (!pendingToast) {
      return;
    }

    sessionStorage.removeItem(PENDING_AUTH_TOAST_KEY);

    if (pendingToast === ACCOUNT_BLOCKED_ERROR_CODE) {
      toast({
        title: t("account_blocked.title", { ns: "errors" }),
        description: t("account_blocked.description", { ns: "errors" }),
        variant: "error",
      });
      return;
    }

    if (pendingToast === LOGIN_BLOCKED_ERROR_CODE) {
      toast({
        title: t("login_blocked.title", { ns: "errors" }),
        description: t("login_blocked.description", { ns: "errors" }),
        variant: "error",
      });
    }
  }, [t, toast]);

  React.useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const id = window.setTimeout(
      () => setResendCooldownSec((current) => Math.max(0, current - 1)),
      1000,
    );
    return () => window.clearTimeout(id);
  }, [resendCooldownSec]);

  const safeNavigate = (to: string) => {
    if (navigatedRef.current) return;
    const current = location.pathname + location.search + location.hash;
    if (!to || to === "/login" || to === current) {
      to = "/dashboard";
    }
    navigatedRef.current = true;
    navigate(to, { replace: true });
  };

  const handleLoginSuccess = (resp: any) => {
    const data = resp?.data ?? resp;

    if (data?.requires_store_selection && Array.isArray(data?.stores)) {
      const stores = data.stores.map((s: any) => ({
        id: s.membership_uuid || s.uuid,
        name: s.name || s.store?.name || s.store_name || "",
        email: s.email || "",
        logo_url: s.logo_url || null,
        is_primary: s.is_primary ?? false,
        is_blocked: s.is_blocked ?? false,
        login_blocked: s.login_blocked ?? false,
        read_only: s.read_only ?? false,
      }));
      const from =
        (location.state as any)?.from ||
        localStorage.getItem("redirectAfterEmailVerification");
      navigatedRef.current = true;
      navigate("/select-store", {
        replace: true,
        state: { stores, from },
      });
      return;
    }

    if (!data?.email_verified_at) {
      localStorage.setItem("@loginTimestamp", Date.now().toString());
      return safeNavigate("/verify-email");
    }

    const from =
      (location.state as any)?.from ||
      localStorage.getItem("redirectAfterEmailVerification");

    if (isInvitationRedirect(from)) {
      setPostLoginRedirect(from);
      return;
    }

    if (data?.onboarding_step !== "finished") {
      localStorage.setItem(
        "welcomeModalOpen",
        JSON.stringify({ isOpen: true }),
      );
      return safeNavigate("/onboarding");
    }

    const redirectTo = typeof from === "string" ? from : "/dashboard";
    return safeNavigate(redirectTo);
  };

  const handleLoginError = (error: AxiosError<AxiosErrorResponse>) => {
    if (
      error.response?.status === 403 &&
      error.response?.data?.error_code === ACCOUNT_BLOCKED_ERROR_CODE
    ) {
      toast({
        title: t("account_blocked.title", { ns: "errors" }),
        description: t("account_blocked.description", { ns: "errors" }),
        variant: "error",
      });
      return;
    }

    if (
      error.response?.status === 401 &&
      error.response?.data?.error_code === LOGIN_BLOCKED_ERROR_CODE
    ) {
      toast({
        title: t("login_blocked.title", { ns: "errors" }),
        description: t("login_blocked.description", { ns: "errors" }),
        variant: "error",
      });
      return;
    }

    if (error.response?.status === 422 || error.response?.status === 401) {
      toast({
        description: t("login_error", { ns: "common" }),
        variant: "error",
      });
      return;
    }

    const apiMessage = extractLoginErrorMessage(error);
    toast({ description: apiMessage || error?.message });
  };

  const openTwoFactorStep = (
    response: unknown,
    credentials: ILogin,
  ) => {
    const body = getAccountLoginBody(response);
    setTwoFactorState({
      challengeId: String(body?.challenge_id ?? ""),
      expiresIn: Number(body?.expires_in ?? 300),
      email: credentials.email,
      password: credentials.password,
    });
    setTwoFactorError(null);
    setResendCooldownSec(RESEND_COOLDOWN_SEC);
    setStep("two_factor");
  };

  const {
    mutate: submitCredentials,
    isPending: isSigning,
    isError,
    isSuccess,
  } = useMutation({
    mutationFn: async (data: ILogin) => signInAccount(data.email, data.password),
    onSuccess: (response, variables) => {
      if (isLoginTwoFactorRequired(response)) {
        openTwoFactorStep(response, variables);
        return;
      }
      handleLoginSuccess(response);
    },
    onError: handleLoginError,
    retry: 0,
  });

  const {
    mutate: verifyTwoFactor,
    isPending: isVerifyingTwoFactor,
  } = useMutation({
    mutationFn: async (code: string) => {
      if (!twoFactorState) {
        throw new Error("missing_two_factor_state");
      }

      return signInAccount(
        twoFactorState.email,
        twoFactorState.password,
        {
          challenge_id: twoFactorState.challengeId,
          code,
        },
      );
    },
    onSuccess: (response) => {
      if (isLoginTwoFactorRequired(response)) {
        const body = getAccountLoginBody(response);
        setTwoFactorState((current) =>
          current
            ? {
                ...current,
                challengeId: String(body?.challenge_id ?? current.challengeId),
                expiresIn: Number(body?.expires_in ?? current.expiresIn),
              }
            : current,
        );
        setTwoFactorError(
          t("securityChallengeInvalidOrExpiredToken", { ns: "common" }),
        );
        return;
      }

      setTwoFactorError(null);
      handleLoginSuccess(response);
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      const apiMessage = extractLoginErrorMessage(error);
      if (apiMessage.toLowerCase().includes("code")) {
        setTwoFactorError(t("securityChallengeInvalidCode", { ns: "common" }));
        return;
      }
      if (
        apiMessage.toLowerCase().includes("expired") ||
        apiMessage.toLowerCase().includes("invalid")
      ) {
        setTwoFactorError(
          t("securityChallengeInvalidOrExpiredToken", { ns: "common" }),
        );
        return;
      }
      setTwoFactorError(
        apiMessage || t("securityChallengeGenericError", { ns: "common" }),
      );
    },
    retry: 0,
  });

  const { mutate: resendTwoFactor, isPending: isResendingTwoFactor } =
    useMutation({
      mutationFn: async () => {
        if (!twoFactorState) {
          throw new Error("missing_two_factor_state");
        }

        return signInAccount(twoFactorState.email, twoFactorState.password);
      },
      onSuccess: (response) => {
        if (!isLoginTwoFactorRequired(response)) {
          handleLoginSuccess(response);
          return;
        }

        const body = getAccountLoginBody(response);
        setTwoFactorState((current) =>
          current
            ? {
                ...current,
                challengeId: String(body?.challenge_id ?? current.challengeId),
                expiresIn: Number(body?.expires_in ?? current.expiresIn),
              }
            : current,
        );
        setTwoFactorError(null);
        setResendCooldownSec(RESEND_COOLDOWN_SEC);
      },
      onError: (error: AxiosError<AxiosErrorResponse>) => {
        const apiMessage = extractLoginErrorMessage(error);
        setTwoFactorError(
          apiMessage ||
            t("securityChallengeRateLimited", { ns: "common" }),
        );
      },
      retry: 0,
    });

  const onSubmit = (data: ILogin) => submitCredentials(data);

  const handleBackToCredentials = () => {
    setStep("credentials");
    setTwoFactorState(null);
    setTwoFactorError(null);
    setResendCooldownSec(0);
  };

  return {
    step,
    onSubmit,
    handleSubmit,
    register,
    setValue,
    getValues,
    errors,
    isLoading: isSigning || isSubmitting,
    isVerifyingTwoFactor,
    isResendingTwoFactor,
    isError,
    isSuccess,
    inviteEmail,
    inviteToken,
    twoFactorEmail: twoFactorState?.email ?? getValues("email"),
    twoFactorError,
    resendCooldownSec,
    verifyTwoFactor,
    resendTwoFactor,
    handleBackToCredentials,
  };
};
